import maplibregl from 'maplibre-gl';
import { hexPricePopup, hexSqftPopup, meanPriceCollection, priceChangeCollection, priceChangePopup, priceChangeSqftPopup } from './hexCollections';
import { readMeanPriceScale, readPriceChangeScale } from './mapScales';
import type { MeanPriceStop, PriceChangeStop } from './mapScales';

export type { MeanPriceStop, PriceChangeStop } from './mapScales';
export { readMeanPriceScale, readPriceChangeScale } from './mapScales';

export type HexLayer = 'meanPrice' | 'priceChange';
export type HexUnit = 'nominal' | 'perSqft';

export type MeanPriceViews = { nominal: MeanPriceStop[]; perSqft: MeanPriceStop[] };
export type PriceChangeViews = { nominal: PriceChangeStop[]; perSqft: PriceChangeStop[] };

const emptyMean = (): MeanPriceViews => ({ nominal: [], perSqft: [] });
const emptyChange = (): PriceChangeViews => ({ nominal: [], perSqft: [] });

// API base URL (same logic as api.ts)
const API_BASE = window.location.hostname === 'localhost' 
  ? 'http://localhost:5000/api' 
  : '/api';

// Track if event handlers have been added (per map instance)
const handlerMap = new WeakMap<maplibregl.Map, boolean>();
const changeHandlerMap = new WeakMap<maplibregl.Map, boolean>();
let activePopup: maplibregl.Popup | null = null;
let activeUnit: HexUnit = 'nominal';
let meanViews: MeanPriceViews = emptyMean();
let changeViews: PriceChangeViews = emptyChange();

export const closeHexPopup = (): void => {
  activePopup?.remove();
  activePopup = null;
};

const showPopup = (map: maplibregl.Map, lngLat: maplibregl.LngLat, html: string): void => {
  closeHexPopup();
  activePopup = new maplibregl.Popup().setLngLat(lngLat).setHTML(html).addTo(map);
};

const formatPercent = (pct: number): string => {
  const rounded = Number(pct.toFixed(1));
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
};

const interpolateFill = (
  property: string,
  stops: Array<[number, string]>,
): maplibregl.ExpressionSpecification | null => {
  if (stops.length < 2) {
    return null;
  }
  const expression: unknown[] = ['interpolate', ['linear'], ['get', property]];
  for (const [value, color] of stops) {
    expression.push(value, color);
  }
  return expression as maplibregl.ExpressionSpecification;
};

const syncFill = (
  map: maplibregl.Map,
  layerId: string,
  sourceId: string,
  property: string,
  stops: Array<[number, string]>,
  hidden: boolean,
): void => {
  const fillColor = interpolateFill(property, stops);
  const filter: maplibregl.FilterSpecification = ['!=', ['get', property], null];
  if (fillColor && !map.getLayer(layerId)) {
    map.addLayer({
      id: layerId,
      type: 'fill',
      source: sourceId,
      filter,
      layout: { visibility: hidden ? 'none' : 'visible' },
      paint: { 'fill-color': fillColor, 'fill-opacity': 0.6 },
    });
    return;
  }
  if (!map.getLayer(layerId)) {
    return;
  }
  if (!fillColor) {
    map.removeLayer(layerId);
    return;
  }
  map.setFilter(layerId, filter);
  map.setPaintProperty(layerId, 'fill-color', fillColor);
  map.setLayoutProperty(layerId, 'visibility', hidden ? 'none' : 'visible');
};

const meanPairs = (stops: MeanPriceStop[]): Array<[number, string]> =>
  stops.map((stop) => [stop.price, stop.color]);

const changePairs = (stops: PriceChangeStop[]): Array<[number, string]> =>
  stops.map((stop) => [stop.pct, stop.color]);

export const applyHexView = (map: maplibregl.Map, layer: HexLayer, unit: HexUnit): void => {
  activeUnit = unit;
  closeHexPopup();
  const perSqft = unit === 'perSqft';
  if (map.getSource('hexagons')) {
    syncFill(
      map,
      'hexagons-fill',
      'hexagons',
      perSqft ? 'meanPricePerSqft' : 'meanPrice',
      meanPairs(perSqft ? meanViews.perSqft : meanViews.nominal),
      layer !== 'meanPrice',
    );
  }
  if (map.getSource('gentrification')) {
    syncFill(
      map,
      'gentrification-fill',
      'gentrification',
      perSqft ? 'annualizedPctPerSqft' : 'annualizedPct',
      changePairs(perSqft ? changeViews.perSqft : changeViews.nominal),
      layer !== 'priceChange',
    );
  }
};

/**
 * Load hexagons GeoJSON and add to map with styling and event handlers.
 * 
 * This function:
 * - Fetches hexagon data from the API
 * - Adds fill and outline layers to the map
 * - Sets up click handlers to show popups with price information
 * - Manages cursor changes on hover
 * 
 * @param map - The MapLibre GL map instance
 */
const formatDollars = (price: number): string =>
  `$${price.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

export const loadHexagonsLayer = async (map: maplibregl.Map): Promise<MeanPriceViews> => {
  try {
    const response = await fetch(`${API_BASE}/hexagons/mean-price`);
    
    if (!response.ok) {
      if (response.status === 404) {
        console.warn('Hexagons GeoJSON not found - file may not exist yet');
        return emptyMean();
      }
      throw new Error(`Failed to load hexagons: ${response.status}`);
    }
    
    const geojson = meanPriceCollection.parse(await response.json());
    meanViews = {
      nominal: readMeanPriceScale(geojson.meanPriceScale),
      perSqft: readMeanPriceScale(geojson.meanPricePerSqftScale),
    };
    
    // Add source with all hexagons (including those without data for outlines)
    if (map.getSource('hexagons')) {
      (map.getSource('hexagons') as maplibregl.GeoJSONSource).setData(geojson);
    } else {
      map.addSource('hexagons', {
        type: 'geojson',
        data: geojson
      });
    }
    
    syncFill(
      map,
      'hexagons-fill',
      'hexagons',
      activeUnit === 'perSqft' ? 'meanPricePerSqft' : 'meanPrice',
      meanPairs(activeUnit === 'perSqft' ? meanViews.perSqft : meanViews.nominal),
      false,
    );

    // Add very thin outline layer for all hexagons (including those without data)
    if (!map.getLayer('hexagons-outline')) {
      map.addLayer({
        id: 'hexagons-outline',
        type: 'line',
        source: 'hexagons',
        paint: {
          'line-color': '#999',
          'line-width': 0.5,
          'line-opacity': 0.5
        }
      });
    }
    
    // Add event handlers only once per map instance
    if (!handlerMap.get(map)) {
      // Add click handler to show popup with mean price
      map.on('click', 'hexagons-fill', (e) => {
        if (e.features && e.features.length > 0) {
          const feature = e.features[0];
          const popupContent = activeUnit === 'perSqft'
            ? sqftPricePopup(feature.properties)
            : nominalPricePopup(feature.properties);
          if (!popupContent) {
            return;
          }
          
          showPopup(map, e.lngLat, popupContent);
        }
      });
      
      // Change cursor on hover
      map.on('mouseenter', 'hexagons-fill', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      
      map.on('mouseleave', 'hexagons-fill', () => {
        map.getCanvas().style.cursor = '';
      });
      
      handlerMap.set(map, true);
    }
    
    console.log('Hexagons layer loaded successfully');
    return meanViews;
  } catch (error) {
    console.error('Error loading hexagons layer:', error);
    return emptyMean();
  }
};

const nominalPricePopup = (properties: unknown): string | null => {
  const parsed = hexPricePopup.safeParse(properties);
  if (!parsed.success) {
    return null;
  }
  const meanPrice = parsed.data.meanPrice;
  const numHouses = parsed.data.numHouses;
  return `
    <div style="padding: 8px;">
      <strong>Mean Price:</strong> ${meanPrice ? formatDollars(meanPrice) : 'N/A'}<br/>
      ${numHouses ? `<strong>Properties:</strong> ${numHouses}` : ''}
    </div>
  `;
};

const sqftPricePopup = (properties: unknown): string | null => {
  const parsed = hexSqftPopup.safeParse(properties);
  if (!parsed.success || parsed.data.meanPricePerSqft === null) {
    return null;
  }
  return `
    <div style="padding: 8px;">
      <strong>Price per sq ft:</strong> ${formatDollars(parsed.data.meanPricePerSqft)}<br/>
      <strong>Sales:</strong> ${parsed.data.numSqftSales}
    </div>
  `;
};

/**
 * Load the price-change GeoJSON and add its fill.
 *
 * Hexes with no rate stay unfilled. The layer starts hidden; setHexLayer
 * shows it when the toggle selects price change.
 */
const changePopup = (properties: unknown): string | null => {
  if (activeUnit === 'perSqft') {
    const parsed = priceChangeSqftPopup.safeParse(properties);
    if (!parsed.success || parsed.data.annualizedPctPerSqft === null) {
      return null;
    }
    return `
      <div style="padding: 8px;">
        <strong>Per sq ft change:</strong> ${formatPercent(parsed.data.annualizedPctPerSqft)} per year<br/>
        <strong>Half-years:</strong> ${parsed.data.numHalvesPerSqft}<br/>
        <strong>Sales:</strong> ${parsed.data.numSalesPerSqft}
      </div>
    `;
  }
  const parsed = priceChangePopup.safeParse(properties);
  if (!parsed.success || parsed.data.annualizedPct === null) {
    return null;
  }
  return `
    <div style="padding: 8px;">
      <strong>Price change:</strong> ${formatPercent(parsed.data.annualizedPct)} per year<br/>
      <strong>Half-years:</strong> ${parsed.data.numHalves}<br/>
      <strong>Sales:</strong> ${parsed.data.numSales}
    </div>
  `;
};

/**
 * Load the price-change GeoJSON and add its fill.
 *
 * Hexes with no rate stay unfilled. The layer starts hidden; applyHexView
 * shows it when the toggle selects price change.
 */
export const loadPriceChangeLayer = async (map: maplibregl.Map): Promise<PriceChangeViews> => {
  try {
    const response = await fetch(`${API_BASE}/hexagons/price-change`);

    if (!response.ok) {
      if (response.status === 404) {
        console.warn('Price-change GeoJSON not found - file may not exist yet');
        return emptyChange();
      }
      throw new Error(`Failed to load price change: ${response.status}`);
    }

    const geojson = priceChangeCollection.parse(await response.json());
    changeViews = {
      nominal: readPriceChangeScale(geojson.priceChangeScale),
      perSqft: readPriceChangeScale(geojson.priceChangePerSqftScale),
    };
    const perSqft = activeUnit === 'perSqft';

    if (map.getSource('gentrification')) {
      (map.getSource('gentrification') as maplibregl.GeoJSONSource).setData(geojson);
    } else {
      map.addSource('gentrification', {
        type: 'geojson',
        data: geojson
      });
    }

    syncFill(
      map,
      'gentrification-fill',
      'gentrification',
      perSqft ? 'annualizedPctPerSqft' : 'annualizedPct',
      changePairs(perSqft ? changeViews.perSqft : changeViews.nominal),
      true,
    );

    if (map.getLayer('gentrification-fill') && !changeHandlerMap.get(map)) {
      map.on('click', 'gentrification-fill', (e) => {
        if (!e.features || e.features.length === 0) {
          return;
        }
        const popupContent = changePopup(e.features[0].properties);
        if (!popupContent) {
          return;
        }
        showPopup(map, e.lngLat, popupContent);
      });

      map.on('mouseenter', 'gentrification-fill', () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', 'gentrification-fill', () => {
        map.getCanvas().style.cursor = '';
      });

      changeHandlerMap.set(map, true);
    }

    console.log('Price-change layer loaded successfully');
    return changeViews;
  } catch (error) {
    console.error('Error loading price-change layer:', error);
    return emptyChange();
  }
};

